import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Shield, Download, Trash2, Cookie, ChevronLeft, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Navigation from '@/components/Navigation';
import FloatingUserMenu from '@/components/FloatingUserMenu';
import SEO from '@/components/SEO';
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const PrivacySettings = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const token = localStorage.getItem('token');

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData || !token) {
      navigate('/account');
      return;
    }
    setUser(JSON.parse(userData));
  }, [navigate, token]);

  const handleDownloadData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users/me/data`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Create a blob and download
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `georgedives-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      alert('Your data has been downloaded successfully!');
    } catch (error) {
      console.error('Error downloading data:', error);
      alert('Failed to download data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      alert('Please type DELETE to confirm');
      return;
    }

    try {
      setLoading(true);
      await axios.delete(`${API}/users/me/account`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Clear local storage and redirect
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('cookieConsent');
      
      alert('Your account has been permanently deleted. We\'re sorry to see you go!');
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      if (error.response?.status === 403) {
        alert(error.response.data.detail || 'You cannot delete your account at this time.');
      } else {
        alert('Failed to delete account. Please try again or contact support.');
      }
      setShowDeleteConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  const handleManageCookies = () => {
    // Clear cookie consent to show banner again
    localStorage.removeItem('cookieConsent');
    localStorage.removeItem('cookieConsentDate');
    alert('Cookie preferences cleared. Please refresh the page to see the cookie consent banner again.');
    window.location.reload();
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen">
      <SEO 
        title="Privacy Settings | George Dives"
        description="Manage your privacy settings, download your data, and control your account"
      />
      
      <Navigation />
      <FloatingUserMenu />

      {/* Header */}
      <section className="pt-24 pb-12 bg-gradient-to-br from-blue-600 to-blue-800">
        <div className="container mx-auto px-4 max-w-4xl">
          <Button
            onClick={() => navigate('/account')}
            variant="outline"
            className="mb-4 border-white text-white hover:bg-white/20"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Account
          </Button>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Privacy Settings
          </h1>
          <p className="text-lg text-white/90">
            Manage your data, privacy, and account settings
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container mx-auto px-4 max-w-4xl space-y-6">
          
          {/* Data Download */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="w-5 h-5 text-cyan-600" />
                Download Your Data
              </CardTitle>
              <CardDescription>
                Export all your personal data stored on our platform (GDPR Right to Access)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                You can download a copy of all your personal data including your profile information, 
                reviews, dive site reports, and activity history. The data will be provided in JSON format.
              </p>
              <Button 
                onClick={handleDownloadData} 
                disabled={loading}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                <Download className="w-4 h-4 mr-2" />
                {loading ? 'Preparing Download...' : 'Download My Data'}
              </Button>
            </CardContent>
          </Card>

          {/* Cookie Management */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cookie className="w-5 h-5 text-amber-600" />
                Manage Cookie Preferences
              </CardTitle>
              <CardDescription>
                Control which cookies and tracking technologies we use
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                You can change your cookie consent preferences at any time. This will reset your 
                current settings and show you the cookie consent banner again.
              </p>
              <Button 
                onClick={handleManageCookies}
                variant="outline"
                className="border-amber-600 text-amber-600 hover:bg-amber-50"
              >
                <Cookie className="w-4 h-4 mr-2" />
                Reset Cookie Preferences
              </Button>
            </CardContent>
          </Card>

          {/* Account Deletion */}
          <Card className="border-red-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Delete Account
              </CardTitle>
              <CardDescription>
                Permanently delete your account and all associated data (GDPR Right to Erasure)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!showDeleteConfirm ? (
                <>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-800">
                      <p className="font-semibold mb-2">Warning: This action cannot be undone!</p>
                      <p>Deleting your account will permanently remove:</p>
                      <ul className="list-disc list-inside mt-2 space-y-1">
                        <li>Your profile and personal information</li>
                        <li>All your reviews and ratings</li>
                        <li>Your dive site reports</li>
                        <li>Your booking history</li>
                      </ul>
                    </div>
                  </div>
                  <Button 
                    onClick={() => setShowDeleteConfirm(true)}
                    variant="destructive"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete My Account
                  </Button>
                </>
              ) : (
                <div className="space-y-4">
                  <p className="text-gray-700 font-semibold">
                    Are you absolutely sure? This action is permanent and cannot be reversed.
                  </p>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type <span className="font-bold">DELETE</span> to confirm:
                    </label>
                    <input
                      type="text"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="Type DELETE here"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button 
                      onClick={handleDeleteAccount}
                      disabled={deleteConfirmText !== 'DELETE' || loading}
                      variant="destructive"
                    >
                      {loading ? 'Deleting...' : 'Confirm Deletion'}
                    </Button>
                    <Button 
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeleteConfirmText('');
                      }}
                      variant="outline"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Privacy Policy Link */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-900 mb-2">
                    Learn more about how we protect your privacy and handle your data.
                  </p>
                  <Button 
                    onClick={() => navigate('/privacy')}
                    variant="link"
                    className="text-blue-600 hover:text-blue-700 p-0 h-auto"
                  >
                    Read our Privacy Policy →
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

        </div>
      </section>
    </div>
  );
};

export default PrivacySettings;
